import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../hooks/useAuth';
import { SOP } from '../../types/sop';
import { toast } from '../ui/use-toast';
import { sopFlagService } from '@/services/sopFlagService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useAppSettings } from '@/context/AppSettingsContext';

interface RemoveFlagDialogProps {
  sop: SOP;
  onFlagRemoved: () => void;
}

/**
 * Dialog component for removing flags from SOPs
 */
export const RemoveFlagDialog: React.FC<RemoveFlagDialogProps> = ({ sop, onFlagRemoved }) => {
  const { user } = useAuth();
  const { appSettings } = useAppSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // If the feature is disabled, don't render the component
  if (!appSettings.showSOPFlaggingFeature) {
    return null;
  }

  const handleRemoveFlag = async () => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      await sopFlagService.removeFlagFromSOP(sop.id);
      setIsOpen(false);
      onFlagRemoved();
      
      toast({
        title: "Success",
        description: "Flag has been removed from the SOP",
      });
    } catch (error) {
      console.error('Error removing flag:', error);
      toast({
        title: "Error",
        description: "Failed to remove flag",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400">
                <CheckCircle className="h-4 w-4" />
                <span className="hidden md:inline">Remove Flag</span>
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Has the issue been resolved? Remove the review flag.</p>
          </TooltipContent>
        </Tooltip>
        
        <DialogContent className="bg-white dark:bg-gray-900" aria-describedby="remove-flag-description">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Remove Review Flag</DialogTitle>
            <DialogDescription id="remove-flag-description" className="text-gray-600 dark:text-gray-400">
              Has the issue with this SOP been resolved? Removing the flag will indicate that it no longer needs review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex gap-2">
              <Button 
                onClick={handleRemoveFlag} 
                disabled={isLoading}
                className="flex-1 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white"
              >
                {isLoading ? 'Removing...' : 'Confirm Removal'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="flex-1 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default RemoveFlagDialog;